import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Typography,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Paper,
  Divider,
  Box,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Tooltip,
  Avatar,
  Card,
  CardContent,
  Checkbox
} from "@mui/material";
import axios from "axios";
import { getToken, base_url } from "../../config/config";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ConstructionIcon from "@mui/icons-material/Construction";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PhoneIcon from "@mui/icons-material/Phone";

// Function to generate avatar color based on name
const stringToColor = (string) => {
  let hash = 0;
  if (!string) return "#1976d2";

  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const EmployeeUpdate = () => {
  const { id } = useParams(); // Get employee ID from URL
  const [employee, setEmployee] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    department: "",
    shift: "",
    skills: [],
    mobileAppAccess: false,
    employmentStartDate:null,
    employmentEndDate:null,
    isFormerEmployee :false
  });

  // Additional state variables
  const [shifts, setShifts] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [departments, setDepartments] = useState([]);

  const navigate = useNavigate();

  // Fetch all necessary data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch employee data
        const employeeResponse = await axios.get(
          `${base_url}/v1/getEmployeeById/${id}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );

        const employeeData = employeeResponse.data;
        console.log("Fetched employee data:", employeeData);

        // Ensure skills is an array
        if (!Array.isArray(employeeData.skills)) {
          employeeData.skills = [];
        }

        setEmployee({
          ...employeeData,
          mobileAppAccess: employeeData.mobileAppAccess || false,
        });

        // Fetch shifts
        const shiftsResponse = await axios.get(`${base_url}/v1/getshifts`);
        setShifts(shiftsResponse.data);

        // Fetch all employees to extract departments
        const allEmployeesResponse = await axios.get(
          `${base_url}/v1/getAllEmployees`
        );
        const uniqueDepartments = [
          ...new Set(
            allEmployeesResponse.data
              .map((emp) => emp.department)
              .filter((dept) => dept && dept !== "")
          ),
        ];
        setDepartments(uniqueDepartments);
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({
          open: true,
          message: "Failed to load employee data",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value , type, checked } = e.target;
    setEmployee({ ...employee, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setEmployee({ ...employee, [name]: checked });
  };

  const handleSkillAdd = () => {
    if (skillInput.trim() && !employee.skills.includes(skillInput.trim())) {
      setEmployee({
        ...employee,
        skills: [...employee.skills, skillInput.trim()],
      });
      setSkillInput("");
    }
  };

  const handleSkillDelete = (skillToDelete) => {
    setEmployee({
      ...employee,
      skills: employee.skills.filter((skill) => skill !== skillToDelete),
    });
  };

  const togglePasswordView = () => {
    setShowPassword(!showPassword);
  };

  const togglePasswordChange = () => {
    setIsChangingPassword(!isChangingPassword);
    if (!isChangingPassword) {
      setPassword("");
      setPasswordConfirm("");
    }
  };

  const validateForm = () => {
    // Basic validation
    if (!employee.name || !employee.email) {
      setSnackbar({
        open: true,
        message: "Name and email are required fields",
        severity: "error",
      });
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(employee.email)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address",
        severity: "error",
      });
      return false;
    }

    // Password validation (if changing password)
    if (isChangingPassword) {
      if (password.length < 6) {
        setSnackbar({
          open: true,
          message: "Password must be at least 6 characters long",
          severity: "error",
        });
        return false;
      }

      if (password !== passwordConfirm) {
        setSnackbar({
          open: true,
          message: "Passwords do not match",
          severity: "error",
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Create payload
      const payload = { ...employee };

      // Add password if changing
      if (isChangingPassword && password) {
        payload.password = password;
      }

      console.log("Submitting payload:", payload);

      // Send update request
      await axios.put(`${base_url}/v1/updateEmployee/${id}`, payload, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      setSnackbar({
        open: true,
        message: "Employee updated successfully",
        severity: "success",
      });

      // Navigate after a short delay to show the success message
      setTimeout(() => {
        navigate("/EmployeeView");
      }, 1500);
    } catch (error) {
      console.error("Error updating employee:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update employee",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Page Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          background: "linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%)",
          color: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: "white",
                color: "#4361ee",
                width: 56,
                height: 56,
              }}
            >
              <PersonIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="500">
                {isLoading
                  ? "Loading Employee..."
                  : `Update Employee: ${employee.name}`}
              </Typography>
              <Typography variant="subtitle1">
                Edit employee information and settings
              </Typography>
            </Box>
          </Box>
          <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/EmployeeView")}
            sx={{
              color: "white",
              borderColor: "white",
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Back to List
          </Button>
          <Tooltip title="Beschäftigungsdaten verwalten">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                // handleOpenEmploymentStatusDialog(employee);
              }}
            >
              <AccessTimeIcon />
            </IconButton>
          </Tooltip>
          </Box>
        </Box>
      </Paper>

      {isLoading ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Loading employee data...
          </Typography>
        </Card>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <PersonIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Personal Information</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Full Name"
                        name="name"
                        value={employee.name}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <PersonIcon
                              color="action"
                              sx={{ mr: 1 }}
                              fontSize="small"
                            />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email"
                        name="email"
                        type="email"
                        value={employee.email || ""}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <EmailIcon
                              color="action"
                              sx={{ mr: 1 }}
                              fontSize="small"
                            />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Phone Number"
                        name="phone"
                        value={employee.phone || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <PhoneIcon
                              color="action"
                              sx={{ mr: 1 }}
                              fontSize="small"
                            />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Position"
                        name="position"
                        value={employee.position || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <WorkIcon
                              color="action"
                              sx={{ mr: 1 }}
                              fontSize="small"
                            />
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="department-label">
                          Department
                        </InputLabel>
                        <Select
                          labelId="department-label"
                          name="department"
                          value={employee.department || ""}
                          onChange={handleInputChange}
                          label="Department"
                          InputProps={{
                            startAdornment: (
                              <MeetingRoomIcon
                                color="action"
                                sx={{ mr: 1 }}
                                fontSize="small"
                              />
                            ),
                          }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {departments.map((dept) => (
                            <MenuItem key={dept} value={dept}>
                              {dept}
                            </MenuItem>
                          ))}
                          {/* Allow custom department */}
                          {employee.department &&
                            !departments.includes(employee.department) && (
                              <MenuItem value={employee.department}>
                                {employee.department} (Custom)
                              </MenuItem>
                            )}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="shift-label">Shift</InputLabel>
                        <Select
                          labelId="shift-label"
                          name="shift"
                          value={employee.shift || ""}
                          onChange={handleInputChange}
                          label="Shift"
                          InputProps={{
                            startAdornment: (
                              <AccessTimeIcon
                                color="action"
                                sx={{ mr: 1 }}
                                fontSize="small"
                              />
                            ),
                          }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {shifts.map((shift) => (
                            <MenuItem key={shift._id} value={shift._id}>
                              {shift.name} ({shift.startTime} - {shift.endTime})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Skills & Mobile Access */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <ConstructionIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Skills & App Access</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Skills
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Add a skill..."
                        value={skillInput}
                        onChange={(e) => setSkillInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSkillAdd();
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleSkillAdd}
                        disabled={!skillInput.trim()}
                        startIcon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {employee.skills.length > 0 ? (
                        employee.skills.map((skill, index) => (
                          <Chip
                            key={index}
                            label={skill}
                            onDelete={() => handleSkillDelete(skill)}
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No skills added
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Mobile App Access
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: employee.mobileAppAccess
                          ? "rgba(46, 204, 113, 0.1)"
                          : "transparent",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={employee.mobileAppAccess}
                            onChange={handleSwitchChange}
                            name="mobileAppAccess"
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1">
                              Enable Mobile App Access
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Allow this employee to log in to the mobile
                              application
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Password Management */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <LockResetIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Employment Status</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Employment Start Date"
                          name="employmentStartDate"
                          type="date"
                          value={employee.employmentStartDate}
                          onChange={handleInputChange}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          helperText="When employee started working"
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Employment End Date"
                          name="employmentEndDate"
                          type="date"
                          value={employee.employmentEndDate}
                          onChange={handleInputChange}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          helperText="Leave empty if still employed"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={employee.isFormerEmployee}
                              onChange={handleInputChange}
                              name="isFormerEmployee"
                              color="primary"
                            />
                          }
                          label="Mark as former employee (will disable login and remove from templates)"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Alert severity="info">
                          Former employees won't be able to log in and won't appear in template maker.
                          Their data is preserved in the system for record-keeping purposes.
                        </Alert>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <LockResetIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Password Management</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant={isChangingPassword ? "contained" : "outlined"}
                      color={isChangingPassword ? "primary" : "inherit"}
                      startIcon={<LockResetIcon />}
                      onClick={togglePasswordChange}
                      sx={{ mb: 2 }}
                    >
                      {isChangingPassword
                        ? "Cancel Password Change"
                        : "Change Password"}
                    </Button>

                    {employee.mobileAppAccess &&
                      !isChangingPassword &&
                      !employee.password && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          This employee has mobile app access enabled but no
                          password set. Please set a password for them to log
                          in.
                        </Alert>
                      )}
                  </Box>

                  {isChangingPassword && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="New Password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          fullWidth
                          required
                          variant="outlined"
                          helperText="Minimum 6 characters"
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                onClick={togglePasswordView}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Confirm New Password"
                          type={showPassword ? "text" : "password"}
                          value={passwordConfirm}
                          onChange={(e) => setPasswordConfirm(e.target.value)}
                          fullWidth
                          required
                          variant="outlined"
                          error={
                            password !== passwordConfirm &&
                            passwordConfirm !== ""
                          }
                          helperText={
                            password !== passwordConfirm &&
                            passwordConfirm !== ""
                              ? "Passwords do not match"
                              : " "
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Box
            sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate("/EmployeeView")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              size="large"
            >
              Save Changes
            </Button>
          </Box>
        </form>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EmployeeUpdate;
